import {
  GetPageResponse,
  fetchRelationOptions,
  clearWorkingRowFieldError,
  updateWorkingBlockData,
  selectBlockCollectionForBlock,
} from "@store/features/crm/pageV2Slice";
import { store } from "@store/index";
import { DataType } from "skedyul-db/browser";
import { Params } from "../../helpers";
import { seedRelationshipOption } from "../../helpers/relationshipOptions";
import { deriveRowId } from "../../helpers/rows";
import { getByPath, getErrorForRowAndPath } from "./utils";
import {
  buildInstanceHref,
  resolveListPathFromContext,
} from "../../helpers/pagePaths";

export type RelationshipValue = {
  id: string | null;
  _meta: { label: string; image: { url?: string; seed?: string } | null };
} | null;

export const createRelationshipCell = (
  field: {
    type: DataType;
    definition?: { type?: string } | null;
    label?: string;
    model?: {
      id?: string;
      handle?: string;
      handlePlural?: string;
    } | null;
    relationshipLink?: {
      id: string;
      cardinality: string;
      targetModelId: string;
      sourceModelId: string;
    } | null;
  },
  params: Params,
  accessorPath: string,
  pageData?: GetPageResponse,
  blockIdParam?: string,
  blockWorkingState?:
    | {
        rows?: Record<
          string,
          {
            fields?: Record<
              string,
              {
                options?: Array<{
                  value: string;
                  label: string;
                  avatar?: { seed?: string; url?: string };
                }>;
              }
            >;
          }
        >;
      }
    | undefined,
  options?: {
    applyRelationshipUpdate?: (ctx: {
      rowIndex: number;
      accessorPath: string;
      value: RelationshipValue;
    }) => void;
  },
) => {
  return {
    component: "Relationship" as const,
    generateProps: (
      row: Record<string, unknown>,
      index: number,
      originalRow?: Record<string, unknown>,
    ) => {
      const relHandle = accessorPath.includes(".")
        ? accessorPath.split(".")[0]
        : undefined;
      const relationRoot = relHandle || accessorPath;

      const resolveTargetModelId = (): string | null => {
        // 1) Use explicit relationship link when available
        if (field?.relationshipLink?.targetModelId) {
          return field.relationshipLink.targetModelId;
        }

        // 2) If the column is explicitly bound to a model, fall back to that
        // model's id. For related-model columns, this is usually what we want.
        if (field?.model?.id) {
          return field.model.id;
        }

        if (!pageData) return null;

        // 3) For nested accessors like "company.owner", infer via the source
        // relationship field on the block's model that targets the column model.
        const relRoot =
          accessorPath.includes(".") && accessorPath.split(".")[0]
            ? accessorPath.split(".")[0]!
            : accessorPath;

        const block =
          pageData.blocks?.find(
            (b: { id?: string }) => b?.id === blockIdParam,
          ) ?? null;
        const sourceModelId =
          (block as { modelId?: string | undefined } | null)?.modelId ?? null;
        if (!sourceModelId) return null;

        const sourceField = (pageData.fields || []).find(
          (f: {
            modelId: string;
            handle: string;
            relationshipLink?: { targetModelId?: string | null } | null;
          }) =>
            f.modelId === sourceModelId &&
            f.handle === relRoot &&
            !!f.relationshipLink?.targetModelId,
        );

        return sourceField?.relationshipLink?.targetModelId ?? null;
      };
      const relatedMeta = getByPath(row, `${relationRoot}._meta`) as
        | { label?: string; image?: { url?: string; seed?: string } | null }
        | undefined;
      const relatedLabel = relatedMeta?.label || undefined;
      const relatedImageUrl =
        relatedMeta?.image &&
        typeof relatedMeta.image === "object" &&
        "url" in relatedMeta.image &&
        typeof (relatedMeta.image as { url?: unknown }).url === "string"
          ? (relatedMeta.image as { url: string }).url
          : undefined;
      const relatedImageSeed =
        relatedMeta?.image &&
        typeof relatedMeta.image === "object" &&
        "seed" in relatedMeta.image &&
        typeof (relatedMeta.image as { seed?: unknown }).seed === "string"
          ? (relatedMeta.image as { seed: string }).seed
          : undefined;

      const resolveId = (
        obj: Record<string, unknown> | undefined,
        path: string,
        relRoot: string | undefined,
      ): string | null => {
        if (!obj) return null;
        const raw = getByPath(obj, path);
        if (typeof raw === "string") return raw || null;
        if (
          raw &&
          typeof raw === "object" &&
          !Array.isArray(raw) &&
          typeof (raw as Record<string, unknown>).id === "string"
        ) {
          return ((raw as Record<string, unknown>).id as string) || null;
        }
        if (relRoot) {
          const nested = getByPath(obj, `${relRoot}.id`);
          if (typeof nested === "string") return nested || null;
        }
        return null;
      };

      const currentId = resolveId(row, accessorPath, relHandle);
      const originalId = resolveId(originalRow, accessorPath, relHandle);

      const wsRowId = deriveRowId(
        (originalRow || row) as Record<string, unknown> | undefined,
        index,
      );
      const wsOpts: Array<{
        value: string;
        label: string;
        avatar?: { seed?: string; url?: string };
      }> =
        blockWorkingState?.rows?.[wsRowId]?.fields?.[accessorPath]?.options ||
        [];
      const wsCurrent = currentId
        ? wsOpts.find((o) => o.value === currentId)
        : undefined;
      const wsOriginal = originalId
        ? wsOpts.find((o) => o.value === originalId)
        : undefined;

      const valueObj: RelationshipValue = currentId
        ? {
            id: currentId,
            _meta: {
              label: wsCurrent?.label ?? relatedLabel ?? "",
              image: wsCurrent?.avatar?.url
                ? { url: wsCurrent.avatar.url }
                : wsCurrent?.avatar?.seed || relatedImageSeed
                  ? {
                      seed: wsCurrent?.avatar?.seed || relatedImageSeed,
                    }
                  : null,
            },
          }
        : null;
      const originalMeta = getByPath(
        originalRow || {},
        `${relationRoot}._meta`,
      ) as
        | { label?: string; image?: { url?: string; seed?: string } | null }
        | undefined;
      const originalValueObj: RelationshipValue = originalId
        ? {
            id: originalId,
            _meta: {
              label: wsOriginal?.label || originalMeta?.label || "",
              image:
                wsOriginal?.avatar &&
                typeof (wsOriginal.avatar as { url?: unknown }).url === "string"
                  ? { url: (wsOriginal.avatar as { url: string }).url }
                  : originalMeta?.image &&
                      typeof originalMeta.image === "object" &&
                      "url" in originalMeta.image &&
                      typeof (originalMeta.image as { url?: unknown }).url ===
                        "string"
                    ? { url: (originalMeta.image as { url: string }).url }
                    : originalMeta?.image &&
                        typeof originalMeta.image === "object" &&
                        "seed" in originalMeta.image &&
                        typeof (originalMeta.image as { seed?: unknown })
                          .seed === "string"
                      ? { seed: (originalMeta.image as { seed: string }).seed }
                      : null,
            },
          }
        : null;

      const fieldError = getErrorForRowAndPath(
        row,
        accessorPath,
        pageData || null,
      );

      return {
        id: accessorPath,
        value: valueObj,
        originalValue: originalValueObj,
        options: (() => {
          const rowId = deriveRowId(
            (originalRow || row) as Record<string, unknown> | undefined,
            index,
          );
          const opts =
            blockWorkingState?.rows?.[rowId]?.fields?.[accessorPath]?.options ||
            [];
          let seeded = seedRelationshipOption(opts, valueObj);
          seeded = seedRelationshipOption(seeded, originalValueObj);
          return seeded;
        })(),
        href: buildInstanceHref({
          subdomain: params.subdomain,
          instancePagePath: `${String(field?.model?.handlePlural ?? "records")}/[id]`,
          instanceId: String(currentId ?? ""),
          fromListPath: resolveListPathFromContext(pageData || null),
        }),
        label:
          wsCurrent?.label ??
          relatedLabel ??
          valueObj?._meta?.label ??
          originalValueObj?._meta?.label ??
          "",
        fieldError,
        onValueChange: (value: string) => {
          function setByPath(
            obj: Record<string, unknown>,
            path: string,
            val: unknown,
          ): Record<string, unknown> {
            const parts = path.split(".");
            if (parts.length === 0) return obj;
            const cloneDeepWithPath = (
              target: Record<string, unknown>,
              idx: number,
            ): Record<string, unknown> => {
              const key = parts[idx]!;
              const isLeaf = idx === parts.length - 1;
              const currentVal =
                target && typeof target === "object" ? target[key] : undefined;
              const nextContainer: Record<string, unknown> =
                currentVal &&
                typeof currentVal === "object" &&
                !Array.isArray(currentVal)
                  ? { ...(currentVal as Record<string, unknown>) }
                  : {};
              const updatedChild = isLeaf
                ? val
                : cloneDeepWithPath(nextContainer, idx + 1);
              return { ...target, [key]: updatedChild };
            };
            return cloneDeepWithPath({ ...(obj || {}) }, 0);
          }

          const state = store.getState();
          const blockId = blockIdParam || "";
          const collection = selectBlockCollectionForBlock(blockId)(state);
          const current = collection?.working;
          const rowId = deriveRowId(
            (originalRow || row) as Record<string, unknown> | undefined,
            index,
          );
          const opts: Array<{
            value: string;
            label: string;
            avatar?: { seed?: string; url?: string };
          }> =
            blockWorkingState?.rows?.[rowId]?.fields?.[accessorPath]?.options ||
            [];
          const selected = opts.find((o) => o.value === value);

          const relRootForUpdate = accessorPath.includes(".")
            ? accessorPath.split(".")[0]!
            : accessorPath;
          const nextRelValue: RelationshipValue = {
            id: value ?? null,
            _meta: {
              label: selected?.label ?? "",
              image: selected?.avatar?.url
                ? { url: selected.avatar.url }
                : selected?.avatar?.seed
                  ? { seed: selected.avatar.seed }
                  : null,
            },
          };

          if (options?.applyRelationshipUpdate) {
            options.applyRelationshipUpdate({
              rowIndex: index,
              accessorPath,
              value: nextRelValue,
            });
            return;
          }

          const applyUpdate = (
            rowObj: Record<string, unknown>,
          ): Record<string, unknown> => {
            let updated = rowObj;
            updated = setByPath(updated, relRootForUpdate, nextRelValue);
            return updated;
          };

          if (Array.isArray(current)) {
            const rows = [...current];
            const existingRow =
              (rows[index] as Record<string, unknown> | undefined) || {};
            const updatedRow = applyUpdate({ ...existingRow });
            const rid = deriveRowId(existingRow, index);
            store.dispatch(
              clearWorkingRowFieldError({
                blockId,
                rowId: rid,
                fieldHandle: accessorPath,
              }),
            );
            rows[index] = updatedRow;
            store.dispatch(updateWorkingBlockData({ blockId, data: rows }));
          } else if (current && typeof current === "object") {
            const baseCurrent = current as Record<string, unknown>;
            const updated = applyUpdate(baseCurrent);
            const rid = String(
              (baseCurrent as { id?: unknown }).id ?? "singleton",
            );
            store.dispatch(
              clearWorkingRowFieldError({
                blockId,
                rowId: rid,
                fieldHandle: accessorPath,
              }),
            );
            store.dispatch(updateWorkingBlockData({ blockId, data: updated }));
          }
        },
        ...(relatedImageUrl || relatedImageSeed
          ? {
              avatar: relatedImageUrl
                ? { url: relatedImageUrl }
                : { seed: relatedImageSeed as string },
              showAvatar: true,
            }
          : {}),
        onOpenChange: async (open: boolean) => {
          if (!open) return [];
          const targetModelId = resolveTargetModelId();
          if (!targetModelId) return [];
          const rowId = deriveRowId(
            (originalRow || row) as Record<string, unknown> | undefined,
            index,
          );
          const action = await store.dispatch(
            fetchRelationOptions({
              blockId: blockIdParam || "",
              rowId,
              fieldHandle: accessorPath,
              targetModelId,
              cache: false,
            }),
          );
          const options = ((action as { payload?: { options?: unknown[] } })
            ?.payload?.options || []) as Array<{
            value: string;
            label: string;
            avatar?: { seed?: string; url?: string };
          }>;
          let seeded = seedRelationshipOption(options, valueObj);
          seeded = seedRelationshipOption(seeded, originalValueObj);
          return seeded;
        },
        onSearch: async (query: string) => {
          const targetModelId = resolveTargetModelId();
          if (!targetModelId) return [];
          const rowId = deriveRowId(
            (originalRow || row) as Record<string, unknown> | undefined,
            index,
          );
          const action = await store.dispatch(
            fetchRelationOptions({
              blockId: blockIdParam || "",
              rowId,
              fieldHandle: accessorPath,
              targetModelId,
              query,
              cache: false,
            }),
          );
          const options = ((action as { payload?: { options?: unknown[] } })
            ?.payload?.options || []) as Array<{
            value: string;
            label: string;
            avatar?: { seed?: string; url?: string };
          }>;
          let seeded = seedRelationshipOption(options, valueObj);
          seeded = seedRelationshipOption(seeded, originalValueObj);
          return seeded;
        },
        searchDebounceMs: 300,
      };
    },
  };
};
